import { useState, useEffect, useCallback } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  OptionProps,
  MultiValueProps,
  MenuProps,
  MenuListProps,
} from "react-select";
import { Option } from "../../interface/Option";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";

interface ProviderOption extends Option {
  type?: string;
  providerName?: string;
  providerId?: string;
}

interface ListOfProviderNameProps {
  onChange: (selected: ProviderOption[]) => void;
  clear?: boolean;
  defaultValue?: ProviderOption[];
  qaIdPrefix?: string;
}

const DEFAULT_QA_PREFIX = "list-of-provider-name";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const ListOfProviderName = ({
  onChange,
  clear,
  defaultValue,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: ListOfProviderNameProps) => {
  const [options, setOptions] = useState<ProviderOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ProviderOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const loadOptions = useCallback(async () => {
    setLoading(true);

    try {
      console.log("debug", providerAxiosInstance.defaults.baseURL);

      const response = await providerAxiosInstance.get("searchProvider", {
        params: {
          name: inputValue,
          active: true,
        },
      });

      const data = response.data?.result || [];

      const formattedOptions: ProviderOption[] = data.map((item: any) => ({
        value: item.provider_id.toString(),
        label: `(${item.provider_id}) ${item.provider_name}`,
        type: item.type,
        providerName: item.provider_name,
        providerId: item.provider_id.toString(),
      }));

      setOptions(formattedOptions);
    } catch (error) {
      console.error("Error loading facility names:", error);
    } finally {
      setLoading(false);
    }
  }, [inputValue]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      loadOptions();
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [inputValue, loadOptions]);

  const handleChange = (selected: MultiValue<ProviderOption>) => {
    const selectedValues = selected as ProviderOption[];
    setSelectedOptions(selectedValues);
    onChange(selectedValues);
  };

  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
    }
  }, [clear]);

  useEffect(() => {
    if (defaultValue) {
      setSelectedOptions(defaultValue);
    }
  }, [defaultValue]);

  const formatOptionLabel = (option: ProviderOption) => (
    <div className="flex justify-between items-center w-full">
      <span>{option.label}</span>

      {option.type && (
        <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded ml-2">
          ({option.type})
        </span>
      )}
    </div>
  );

  const customStyles: StylesConfig<ProviderOption, true> = {
    control: (styles) => ({
      ...styles,
      minHeight: "40px",
      fontSize: "14px",
      borderRadius: "8px",
      cursor: "pointer",
    }),

    valueContainer: (styles) => ({
      ...styles,
      fontSize: "14px",
    }),

    input: (styles) => ({
      ...styles,
      fontSize: "14px",
    }),

    placeholder: (styles) => ({
      ...styles,
      fontSize: "14px",
      color: "#6B7280",
    }),

    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#007BFF",
      color: "#FFFFFF",
      borderRadius: "5px",
    }),

    multiValueLabel: (styles) => ({
      ...styles,
      color: "#FFFFFF",
    }),

    multiValueRemove: (styles) => ({
      ...styles,
      color: "#FFFFFF",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),

    option: (styles, { isSelected, isFocused }) => ({
      ...styles,
      backgroundColor: isSelected
        ? "#007BFF"
        : isFocused
        ? "#E6F0FA"
        : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      fontSize: "14px",
      padding: "8px 10px",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),

    menu: (styles) => ({
      ...styles,
      zIndex: 999999,
      pointerEvents: "auto",
    }),

    menuPortal: (styles) => ({
      ...styles,
      zIndex: 999999,
      pointerEvents: "auto",
    }),

    menuList: (styles) => ({
      ...styles,
      maxHeight: "220px",
      overflowY: "auto",
      pointerEvents: "auto",
    }),
  };

  const Menu = (props: MenuProps<ProviderOption, true>) => (
    <div {...qaAttrs(qaIdPrefix, "menu")}>
      <components.Menu {...props} />
    </div>
  );

  const MenuList = (props: MenuListProps<ProviderOption, true>) => (
    <components.MenuList {...props}>
      <div
        onWheelCapture={(e) => e.stopPropagation()}
        onTouchMoveCapture={(e) => e.stopPropagation()}
      >
        {props.children}
      </div>
    </components.MenuList>
  );

  const OptionComponent = (props: OptionProps<ProviderOption, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `option-${qaSafe(String(props.data.value))}`)}
      data-option-label={props.data.label}
      data-option-value={props.data.value}
      data-provider-id={props.data.providerId || props.data.value}
      data-provider-name={props.data.providerName || ""}
      data-provider-type={props.data.type || ""}
    >
      <components.Option {...props} />
    </div>
  );

  const MultiValueComponent = (
    props: MultiValueProps<ProviderOption, true>
  ) => (
    <div
      {...qaAttrs(qaIdPrefix, `selected-${qaSafe(String(props.data.value))}`)}
      data-selected-label={props.data.label}
      data-selected-value={props.data.value}
      data-provider-id={props.data.providerId || props.data.value}
      data-provider-name={props.data.providerName || ""}
      data-provider-type={props.data.type || ""}
    >
      <components.MultiValue {...props} />
    </div>
  );

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        className={selectQaId}
        aria-label="Provider selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={setInputValue}
        formatOptionLabel={formatOptionLabel}
        placeholder="Type at least 3 letters to search..."
        styles={customStyles}
        components={{
          Menu,
          MenuList,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
        }}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        menuPosition="fixed"
        menuPlacement="bottom"
        menuShouldScrollIntoView={false}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        captureMenuScroll={false}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>
    </div>
  );
};

export default ListOfProviderName;